import React from 'react';
import {
    renderRow, renderCopyTrigger, AnimatedTabs, Card, AdvancedTable, WarningModal,
} from 'components';
import { ExpertStatusOptions, MappedExpertStatus } from 'components/enums';
import { IAttestationSubCommissionDetails, IExpert, IExperts } from 'components/models';
import { formatFIO, formatPhoneNumber } from '_helpers';
import { Tab } from 'react-bootstrap';
import { PaginationImpl } from '../defaultValues';
import { useMultiSearch } from 'hooks';
import { IExpertsFilters } from '../reduxModels';

const AscDetailsInfo = (
  {
      ascDetails, ascExperts, getExperts, ascId,
      ...props
  }: IProps,
) => {
    const [selectedId, selectId] = React.useState<string | null>(null);
    const { filters, changeFilter } = useMultiSearch<IExpertsFilters>(getExperts || (() => true), {
        ascIds: [ascId],
        linkedWithCurrentCommission: false,
    });
    React.useEffect(() => {
        if (getExperts) {
            getExperts(filters);
        }
    }, []);
    if (!ascDetails) return null;
    const infoRows = [
        renderRow('РЕГИОН', renderCopyTrigger(ascDetails.regionName)),
        renderRow('СПЕЦИАЛЬНОСТЬ', renderCopyTrigger(ascDetails.qualificationTitle)),
    ];

    ascDetails.leaders.forEach((l, i) => {
        const status = l.linkedExpertStatuses.find(s => s.ascId === ascDetails.id);
        const mappedStatus = MappedExpertStatus[status ? status.status : ''];
        infoRows.push(
          renderRow(mappedStatus.text, formatFIO(l), 'mt-5'),
          renderRow('МЕСТО РАБОТЫ', l.placeOfWork, undefined, undefined, i),
          renderRow('ДОЛЖНОСТЬ', l.position, undefined, undefined, i),
          renderRow('ТЕЛЕФОН', formatPhoneNumber(l.phone) || '', undefined, undefined, i),
          renderRow('ПОЧТА', l.email, undefined, undefined, i),
        );
    });

    const getStatus = (e: IExpert, id?: string) => {
        if (!id) return '';
        const linked = e.linkedExpertStatuses.find(s => s.ascId === id);

        if (linked) return MappedExpertStatus[linked.status].text;

        return '';
    };

    const header = [
        { title: 'ИМЯ, ФАМИЛИЯ', fieldName: 'fullName' },
        {
            title: 'СТАТУС', fieldName: 'status', columnStyles: { width: 215 }, selectOptions: ExpertStatusOptions,
        },
        { title: 'ТЕЛЕФОН', fieldName: 'phone', columnStyles: { width: 165 } },
    ];

    const pagination = new PaginationImpl(ascExperts);
    const selectedExpert = ascExperts ? ascExperts.items.find(i => i.id === selectedId) : undefined;

    return props.withoutMembers ? (
        <>
            {infoRows}
        </>
    ) : (
      <AnimatedTabs>
          <Tab eventKey="info" title="Информация">
              <Card
                withoutBorder
                title="Информация об АПК"
                category="Общая информация об аккредитационной подкомиссии"
              >
                  {infoRows}
              </Card>
          </Tab>

          <Tab eventKey="experts" title="Список членов">
              <Card
                withoutBorder
                withoutPadding
                title="Члены АПК"
                category="Список членов, входящих в состав АПК"
                extHeader={ascExperts ? [
                    { label: 'ВСЕГО ЧЛЕНОВ АПК', value: `${ascExperts.total} чел.` },
                ] : undefined}
              >
                  <AdvancedTable
                    data={ascExperts ? ascExperts.items.map(e => ({
                        id: e.id,
                        columns: [
                            formatFIO(e),
                            getStatus(e, ascId),
                            formatPhoneNumber(e.phone) || e.phone,
                        ],
                    })) : []}
                    tableHeaders={header}
                    handleRowClick={selectId}
                    pageSize={pagination.pageSize}
                    page={pagination.page}
                    total={pagination.total}
                    handlePageChange={p => changeFilter({
                        page: p.page,
                        pageSize: p.perPage,
                    })}
                    handleSearch={(f, t) => changeFilter({
                        searchField: f,
                        searchTerm: t,
                    })}
                    handleOrder={f => changeFilter({
                        sortOrder: f === filters.sortField ? !filters.sortOrder : true,
                        sortField: f,
                    })}
                  />
              </Card>

              {props.renderExpertDetails && (
                <WarningModal
                  card
                  large
                  title={selectedExpert ? formatFIO(selectedExpert) : ''}
                  show={!!selectedId}
                  onHide={() => selectId(null)}
                >
                    {selectedId && props.renderExpertDetails(selectedId)}
                </WarningModal>
              )}
          </Tab>
      </AnimatedTabs>
    );
};

export default AscDetailsInfo;

interface IProps {
    ascId: string;
    ascDetails: IAttestationSubCommissionDetails | null;
    ascExperts?: IExperts | null;
    getExperts?: (payload: IExpertsFilters) => void;
    renderExpertDetails?: (id: string) => React.ReactNode;
    withoutMembers?: boolean;
}
